import { useNavigate } from 'react-router-dom';
import {
  Landmark, Briefcase, TrendingUp, Receipt,
  ChevronRight, CheckCircle2,
} from 'lucide-react';

const STEPS = [
  {
    key: 'accounts',
    title: 'Add Your Accounts',
    description: 'Add bank accounts, other assets, liabilities, and credit cards so Meridian can track your net worth.',
    icon: Landmark,
    route: '/finance/master-accounts',
  },
  {
    key: 'paycheck',
    title: 'Set Up Your Paycheck',
    description: 'Enter your paycheck details once and Meridian will automatically track your income going forward.',
    icon: Briefcase,
    route: '/finance/paycheck',
  },
  {
    key: 'investments',
    title: 'Add Your Portfolio',
    description: 'Add your investment holdings (stocks, ETFs, bonds) and Meridian will pull live prices automatically.',
    icon: TrendingUp,
    route: '/finance/investments',
  },
  {
    key: 'subscriptions',
    title: 'Track Subscriptions',
    description: 'Add your recurring subscriptions to see your total monthly and annual costs at a glance.',
    icon: Receipt,
    route: '/finance/subscriptions',
  },
];

export default function OnboardingGuide({ completedSteps, onDismiss }) {
  const navigate = useNavigate();
  const allDone = STEPS.every((s) => completedSteps[s.key]);

  return (
    <div className="max-w-lg mx-auto py-12">
      {/* Hero */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Welcome to Meridian</h1>
        <p className="text-sm text-text-secondary mt-2">
          Get started in a few steps so your dashboard is ready to go.
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((step) => (
          <div
            key={step.key}
            className={`h-1.5 flex-1 rounded-full transition-colors ${completedSteps[step.key] ? 'bg-accent' : 'bg-border'}`}
          />
        ))}
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {STEPS.map((step) => {
          const done = completedSteps[step.key];
          const Icon = step.icon;

          return (
            <button
              key={step.key}
              onClick={() => navigate(step.route)}
              className="w-full flex items-center gap-3 bg-bg-card rounded-xl p-4 border border-border/40 hover:border-accent/40 transition-colors text-left"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${done ? 'bg-success/15' : 'bg-accent/15'}`}>
                <Icon className={`w-[18px] h-[18px] ${done ? 'text-success' : 'text-accent'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${done ? 'text-text-secondary' : 'text-text-primary'}`}>
                  {step.title}
                </p>
                <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                  {step.description}
                </p>
              </div>
              {done ? (
                <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
              ) : (
                <ChevronRight className="w-[18px] h-[18px] text-text-muted shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      {/* Action */}
      {allDone ? (
        <button
          onClick={onDismiss}
          className="mt-6 w-full bg-accent text-white text-sm font-semibold rounded-xl py-3 hover:opacity-90 transition-opacity"
        >
          Go to Dashboard
        </button>
      ) : (
        <button
          onClick={onDismiss}
          className="mt-5 w-full text-center py-2 text-xs text-text-muted hover:text-text-secondary transition-colors"
        >
          Skip for now
        </button>
      )}
    </div>
  );
}
